// ReturnPolicy.jsx

import React, { useState, useEffect } from "react";

const sections = [
  {
    id: "01",
    title: "Return Eligibility",
    content:
      "Customers may request a return within 7 days of delivery for eligible products purchased directly from Tecniqa or authorized sellers.",
  },
  {
    id: "02",
    title: "Product Condition",
    content:
      "Returned products must be unused, undamaged, and in original packaging with all accessories, manuals, labels, and invoices intact.",
  },
  {
    id: "03",
    title: "Non-returnable Items",
    content:
      "Installed products, chemicals, consumables, customized items, and products damaged after delivery are not eligible for return.",
  },
  {
    id: "04",
    title: "Damaged or Incorrect Products",
    content:
      "Customers receiving damaged, defective, or incorrect products must notify Tecniqa within 48 hours of delivery along with supporting images or videos.",
  },
  {
    id: "05",
    title: "Inspection & Approval",
    content:
      "All returned products are subject to technical inspection before approval of refunds, replacements, or exchanges.",
  },
  {
    id: "06",
    title: "Refund Process",
    content:
      "Approved refunds are processed within 5–7 business days through the original payment method or store credit, depending on the order type.",
  },
  {
    id: "07",
    title: "Shipping Responsibility",
    content:
      "Return shipping charges may apply unless the return is due to damaged, defective, or incorrectly delivered products.",
  },
  {
    id: "08",
    title: "Order Cancellation",
    content:
      "Orders may be cancelled before dispatch. Cancellation requests after shipment may not be accepted.",
  },
];

const trustItems = [
  "Easy Return Process",
  "Secure Refund Handling",
  "Professional HVAC Support",
  "Trusted Industrial Products",
];

export default function ReturnPolicy() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Return Policy | Tecniqa";
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        :root {
          --dark: #1B211A;
          --green: #628141;
          --green-soft: #8BAE66;
          --cream: #EBD5AB;
          --white: #ffffff;
        }

        .rp-root {
          font-family: 'DM Sans', 'Segoe UI', sans-serif;
          background: #f7f7f5;
          min-height: 100vh;
        }

        /* ── HERO ── */
        .rp-hero {
          position: relative;
          padding: 56px 20px 100px;
          text-align: center;
          background: linear-gradient(145deg, #07140b 0%, #0d2414 50%, #17331f 100%);
          overflow: hidden;
        }

        .rp-hero-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(139, 174, 102, .07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 174, 102, .07) 1px, transparent 1px);
          background-size: 56px 56px;
          pointer-events: none;
        }

        .rp-hero-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
        }

        .rp-orb1 {
          width: 380px; height: 380px;
          top: -120px; left: -80px;
          background: rgba(139, 174, 102, .16);
        }

        .rp-orb2 {
          width: 280px; height: 280px;
          bottom: -80px; right: -60px;
          background: rgba(98, 129, 65, .18);
        }

        .rp-hero-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 900px;
          margin: 0 auto;
        }

        .rp-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(139, 174, 102, .12);
          border: 1px solid rgba(139, 174, 102, .26);
          border-radius: 999px;
          padding: 6px 18px;
          margin-bottom: 22px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
          color: #8BAE66;
        }

        .rp-badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #8BAE66;
          box-shadow: 0 0 6px #8BAE66;
          animation: rpPulse 1.6s ease infinite;
        }

        @keyframes rpPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .45; transform: scale(.7); }
        }

        .rp-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.2rem, 5vw, 3.8rem);
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.02em;
          line-height: 1.1;
          margin-bottom: 14px;
        }

        .rp-hero-title span { color: #8BAE66; }

        .rp-hero-sub {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.62);
          font-weight: 300;
          line-height: 1.8;
          max-width: 500px;
          margin-bottom: 24px;
        }

        .rp-hero-meta {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .rp-meta-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(98, 129, 65, 0.18);
          border: 1px solid rgba(98, 129, 65, 0.32);
          color: #8BAE66;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 14px;
          border-radius: 20px;
          letter-spacing: 0.05em;
        }

        /* ── TRUST STRIP ── */
        .rp-trust {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: #fff;
          border-bottom: 1px solid #ebebeb;
          position: relative;
          z-index: 2;
        }

        .rp-trust-item {
          padding: 20px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #1B211A;
          border-right: 1px solid #ebebeb;
          letter-spacing: 0.01em;
        }

        .rp-trust-item:last-child { border-right: none; }

        .rp-trust-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #628141;
          flex-shrink: 0;
        }

        /* ── BODY ── */
        .rp-body {
          max-width: 920px;
          margin: 0 auto;
          padding: 50px 32px 80px;
        }

        /* ── INTRO CARD ── */
        .rp-intro-card {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #ebebeb;
          border-left: 4px solid #628141;
          padding: 24px 28px;
          margin-top: -32px;
          margin-bottom: 36px;
          position: relative;
          z-index: 2;
          box-shadow: 0 4px 20px rgba(0,0,0,0.07);
        }

        .rp-intro-card p {
          font-size: 14.5px;
          line-height: 1.85;
          color: #555;
          font-weight: 400;
        }

        .rp-intro-card strong {
          color: #111;
          font-weight: 700;
        }

        /* ── ACCORDION ── */
        .rp-sections {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 48px;
        }

        .rp-section {
          background: #fff;
          border: 1px solid #ebebeb;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .rp-section:hover {
          border-color: rgba(98, 129, 65, 0.4);
          box-shadow: 0 2px 12px rgba(0,0,0,0.07);
        }

        .rp-section--open {
          border-color: #628141;
          box-shadow: 0 4px 20px rgba(98, 129, 65, 0.12);
        }

        .rp-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 22px;
          user-select: none;
        }

        .rp-section-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .rp-section-num {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #628141;
          background: rgba(98,129,65,0.1);
          border: 1px solid rgba(98,129,65,0.2);
          border-radius: 6px;
          padding: 3px 8px;
          min-width: 36px;
          text-align: center;
        }

        .rp-section-title {
          font-size: 14.5px;
          font-weight: 600;
          color: #111;
          letter-spacing: 0.01em;
        }

        .rp-chevron {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(98,129,65,0.08);
          border: 1px solid rgba(98,129,65,0.18);
          color: #628141;
          font-size: 18px;
          font-weight: 300;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.2s, color 0.2s;
          line-height: 1;
        }

        .rp-section--open .rp-chevron {
          background: #628141;
          color: #fff;
          border-color: #628141;
        }

        .rp-section-body {
          padding: 0 22px 20px 74px;
          border-top: 1px solid rgba(98, 129, 65, 0.1);
        }

        .rp-section-body p {
          padding-top: 16px;
          font-size: 14px;
          line-height: 1.9;
          color: #666;
          font-weight: 400;
        }

        /* ── FOOTER NOTE ── */
        .rp-footer-note {
          background: linear-gradient(135deg, #1B211A 0%, #253919 40%, #223616 70%, #3e5326 100%);
          border-radius: 14px;
          padding: 28px 32px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.18);
        }

        .rp-footer-note::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #628141, #8BAE66, #628141);
        }

        .rp-footer-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #8BAE66;
        }

        .rp-footer-note p {
          font-size: 14px;
          color: rgba(255,255,255,0.65);
          line-height: 1.7;
          font-weight: 300;
        }

        .rp-footer-note a {
          color: #EBD5AB;
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid rgba(235,213,171,0.3);
          transition: border-color 0.2s;
        }

        .rp-footer-note a:hover { border-color: #EBD5AB; }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .rp-trust { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 640px) {
          .rp-body { padding: 0 16px 60px; }
          .rp-intro-card { margin-top: 15px; padding: 18px 20px; }
          .rp-section-body { padding-left: 22px; }
          .rp-footer-note { padding: 22px 20px; }
          .rp-hero { padding: 40px 16px 80px; }
          .rp-trust { grid-template-columns: 1fr; }
          .rp-trust-item { border-right: none; border-bottom: 1px solid #ebebeb; }
          .rp-trust-item:last-child { border-bottom: none; }
        }
      `}</style>

      <div className="rp-root">

        {/* HERO */}
        <div className="rp-hero">
          <div className="rp-hero-grid" />
          <div className="rp-hero-orb rp-orb1" />
          <div className="rp-hero-orb rp-orb2" />

          <div className="rp-hero-content">
            <div className="rp-hero-badge">
              <div className="rp-badge-dot" />
              Returns &amp; Refunds
            </div>

            <h1 className="rp-hero-title">
              Return Policy,<br />
              <span>Done Right</span>
            </h1>

            <p className="rp-hero-sub">
              Tecniqa strives to provide a smooth and transparent return experience
              while ensuring quality standards for industrial HVACR and refrigeration products.
            </p>

            <div className="rp-hero-meta">
              <span className="rp-meta-badge">Effective: May 2026</span>
              <span className="rp-meta-badge">8 Sections</span>
            </div>
          </div>
        </div>

        {/* TRUST STRIP */}
        <div className="rp-trust">
          {trustItems.map((item, i) => (
            <div className="rp-trust-item" key={i}>
              <div className="rp-trust-dot" />
              {item}
            </div>
          ))}
        </div>

        {/* BODY */}
        <div className="rp-body">

          {/* INTRO CARD */}
          <div className="rp-intro-card">
            <p>
              At <strong>Tecniqa</strong>, we are committed to a fair and hassle-free return
              process. This policy outlines the conditions, timelines, and procedures for
              returning products purchased through the Tecniqa platform. Please read these
              terms carefully before initiating a return request.
            </p>
          </div>

          {/* ACCORDION */}
          <div className="rp-sections">
            {sections.map((sec) => {
              const isOpen = active === sec.id;
              return (
                <div
                  key={sec.id}
                  className={`rp-section${isOpen ? " rp-section--open" : ""}`}
                  onClick={() => setActive(isOpen ? null : sec.id)}
                >
                  <div className="rp-section-header">
                    <div className="rp-section-left">
                      <span className="rp-section-num">{sec.id}</span>
                      <span className="rp-section-title">{sec.title}</span>
                    </div>
                    <span className="rp-chevron">{isOpen ? "−" : "+"}</span>
                  </div>
                  {isOpen && (
                    <div className="rp-section-body">
                      <p>{sec.content}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* FOOTER NOTE */}
          <div className="rp-footer-note">
            <span className="rp-footer-label">Need return assistance?</span>
            <p>
              Contact our support team at{" "}
              <a href="mailto:support@tecniqa.com">support@tecniqa.com</a> or call{" "}
              <a href="tel:+919668246683">+91 9668 246 683</a> during business hours (Mon–Sat, 9 AM – 6 PM).
            </p>
          </div>

        </div>
      </div>
    </>
  );
}